import Button from '@vromo/ui/components/Button';
import React, { useState, useEffect } from 'react';
import useViewModel from '@vromo/hooks/useViewModel';
import { serialize, update } from 'serializr';

import Modal, { ModalHeader, ModalContent, ModalActions } from '@vromo/ui/components/Modal';
import { useModals } from '@vromo/ui/components/Modals';
import { useToasts } from '@vromo/ui/components/Toasts';
import Alert from '@vromo/ui/components/Alert';
import Title from '@vromo/ui/components/Title';

import { useStore } from '@vromo/mobx-react';
import { observer } from 'mobx-react-lite';
import { Site } from '@vromo/models';

import { Tabs, Tab } from '@vromo/ui/components/Tabs';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import EditGeneral from './panels/General';
import EditLocation from './panels/Location';
import EditAdvanced from './panels/Advanced';
import SiteTools from './SiteTools';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import { RootStore } from '@vromo/dashboard/src/stores/RootStore';
import { runInAction } from 'mobx';
import { qaLabelTestSites } from './../../../../../common-qa-labels/sites';

type Props = {
    open: boolean;
    close: () => void;
    site: Site;
    isNew: boolean;
    sortedPolicyOptions: { label: string; value: number }[];
};

const tabOrder = ['general', 'location', 'advanced'];

export default observer(function NewSite({ open, close, site, isNew, sortedPolicyOptions }: Props) {
    const {
        authStore: { graph, role },
        siteStore
    } = useStore<RootStore>('rootStore');

    const [editedSite, editedSitePatch] = useViewModel(site);
    const [currentTab, setCurrentTab] = useState<string>('general');
    const [showAlert, setShowAlert] = useState<boolean>(false);

    const modals = useModals();
    const showToast = useToasts();

    useEffect(() => {
        if (open) {
            setCurrentTab('general');
        }
    }, [open]);

    function deleteSite() {
        graph.root
            .graph()
            .roles(role.id)
            .sites(site.id!)
            .delete()
            .then(() => {
                close();
                showToast(`Removed ${site.contact?.name}`);
                runInAction(() => siteStore.sites.remove((s: Site) => s.id === site.id));
            })
            .catch((e) => console.error(e));
    }

    async function confirmDelete() {
        if (await modals.confirm('Delete Site', 'Are you sure you want to delete this site?')) {
            deleteSite();
        }
    }

    async function confirmClose() {
        if (
            !isNew &&
            editedSite.isDirty() &&
            (await modals.confirm(
                'Unsaved Changes',
                'Are you sure you want to leave without saving your changes?'
            ))
        ) {
            close();
            editedSite.reset();
            setShowAlert(false);
        } else if (isNew || (!isNew && !editedSite.isDirty())) {
            close();
            setShowAlert(false);
        }
    }

    function isValidSite(site: Site) {
        const isValidNumber = site.contact?.phone?.length
            ? !!parsePhoneNumberFromString(site.contact.phone)?.isValid()
            : !site.contact?.phone;

        return !!(
            site.group &&
            site.contact?.name &&
            isValidNumber &&
            site.contact?.address &&
            site.coords?.lat &&
            site.coords?.lon
        );
    }

    function saveSite() {
        if (isValidSite(editedSite)) {
            const serializedSite = serialize(editedSite);

            delete serializedSite.source;
            delete serializedSite.created;

            graph.root
                .graph()
                .roles(role.id)
                .sites()
                .post([
                    {
                        ...editedSitePatch,
                        ...(serializedSite.policy && {
                            policy: { id: serializedSite.policy.id }
                        }),
                        blueprint: editedSite?.blueprint || null,
                        ...(!isNew && {
                            id: editedSite.id,
                            contact: serializedSite.contact,
                            coords: serializedSite.coords
                        })
                    }
                ])
                .then(({ items }) => {
                    update(siteStore.sites, { items, type: isNew ? 'create' : 'update' });
                    close();
                    showToast(
                        `Successfully ${isNew ? 'added' : 'updated'} ${items[0]!.contact?.name}`,
                        'success'
                    );
                })
                .catch((e) => {
                    console.error(e);
                    showToast(
                        `Something went wrong. Please try ${
                            isNew ? 'adding' : 'updated'
                        } the site again.`,
                        'error'
                    );
                });
        } else {
            setShowAlert(true);
        }
    }

    function nextTab() {
        const currentTabIx = tabOrder.findIndex((val) => val === currentTab);
        setCurrentTab(tabOrder[currentTabIx + 1]!);
    }

    return (
        <Modal isOpen={open} onRequestClose={confirmClose}>
            <ModalHeader>
                <Title>{isNew ? 'New' : 'Edit'} Site</Title>
            </ModalHeader>
            <ModalContent style={{ minHeight: 450 }}>
                <Tabs currentTab={currentTab} onChange={setCurrentTab}>
                    <Tab id="general">General</Tab>
                    <Tab id="location">Location</Tab>
                    <Tab id="advanced">Advanced</Tab>
                    {!isNew && <Tab id="tools">Tools</Tab>}
                </Tabs>
                {showAlert ? (
                    <Alert danger>
                        Make sure your site has a valid name, group and address before saving.
                    </Alert>
                ) : null}
                {currentTab === 'general' && (
                    <EditGeneral editedSite={editedSite} policyOptions={sortedPolicyOptions} />
                )}
                {currentTab === 'location' && <EditLocation editedSite={editedSite} />}
                {currentTab === 'advanced' && (
                    <EditAdvanced editedSite={editedSite} isNew={isNew} />
                )}
                {currentTab === 'tools' && <SiteTools editedSite={editedSite} />}
            </ModalContent>
            {isNew ? (
                <ModalActions>
                    <Button onClick={confirmClose} data-testid={qaLabelTestSites.addSiteModal.cancelButton}>Cancel</Button>
                    {currentTab !== 'advanced' ? (
                        <Button primary onClick={nextTab} data-testid={qaLabelTestSites.addSiteModal.nextButton}>
                            Next <FontAwesome icon="angle-right" style={{ marginLeft: 3 }} />
                        </Button>
                    ) : (
                        <Button primary onClick={saveSite} data-testid={qaLabelTestSites.addSiteModal.saveSiteButton}>
                            Save Site
                        </Button>
                    )}
                </ModalActions>
            ) : (
                <ModalActions>
                    <Button data-testid={qaLabelTestSites.editSiteModal.deleteSiteButton} destructive style={{ marginRight: 'auto' }} onClick={confirmDelete}>
                        Delete
                    </Button>
                    <Button onClick={confirmClose} data-testid={qaLabelTestSites.editSiteModal.cancelButton}>Cancel</Button>
                    <Button primary onClick={saveSite} data-testid={qaLabelTestSites.editSiteModal.saveSiteButton}>
                        Save Site
                    </Button>
                </ModalActions>
            )}
        </Modal>
    );
});
